import * as PropTypes from "prop-types";
import * as React from "react";
import FlatButton from "material-ui/FlatButton";
import IconButton from "material-ui/IconButton";
import Popover from "@material-ui/core/Popover";
import DropDownIcon from "components/icons/caret";
import theme from "styles/theme";
import color from "color";
import { $, merge } from "glamor";
import classNames from "classnames";

const styles = {
    base: {
        border: "1px solid " + (theme.palette as any).lightGrey,
        borderRadius: "20px",
        textTransform: "none",
        whiteSpace: "nowrap",
    },
    basePrimary: {
        // border: "0px"
        border: "1px solid " + (theme.palette as any).primary1Color,
    },
    baseLarge: {
        borderRadius: "25px",
        height: "50px",
    },
    chevron: {
        top: "50%",
        right: "8px",
        position: "absolute",
        zIndex: 2,
        display: "block",
        transform: "translateY(-50%)",
        marginRight: "0px",
    },
    label: {
        padding: "0 32px",
        color: (theme.palette as any).darkGrey,
        fontWeight: 400,
        position: "relative",
        top: "-1px",
    },
    labelMobile: {
        padding: "0 16px",
    },
    labelPrimary: {
        color: "white",
    },
    labelLarge: {
        fontSize: "20px",
    },
    dropdownLabel: {
        padding: "0 8px 0 16px",
    },
};

interface IProps {
    [x: string]: any;
}

class Button extends React.PureComponent<IProps, any> {
    static propTypes = {
        primary: PropTypes.bool,
        large: PropTypes.bool,
        dropdown: PropTypes.bool,
        style: PropTypes.object,
        disabled: PropTypes.bool,
        segmented: PropTypes.bool,
        menu: PropTypes.node,
        viewport: PropTypes.string,
        alignMenu: PropTypes.string,
        anchorOrigin: PropTypes.object,
        onCloseMenu: PropTypes.func,
        dropdownIconStyles: PropTypes.object,
    };

    static defaultProps = {
        primary: false,
        large: false,
        dropdown: false,
        segmented: true,
        alignMenu: "left",
        style: {},
    };

    static displayName = "Button";

    container = null;

    state = {
        menuOpen: false,
    };

    private styles = {
        base: merge(
            {
                display: "inline-block",
                position: "relative",
            },
            $(" .buttonIsOpen", {
                zIndex: 2,
            }),
            $(" .buttonSegmented", {
                position: "absolute",
                top: "-1px",
                right: "0px",
                zIndex: 3,
            }),
            $(" .buttonSeparator", {
                width: "1px",
                height: "32px",
                top: "5px",
                left: "0px",
                position: "absolute",
                zIndex: 4,
                backgroundColor: (theme.palette as any).lightGrey,
            })
        ),
        paper: merge({
            zIndex: "2500 !important",
        }),
    };

    handleMenuOpen = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.setState({ menuOpen: true });
    };

    handleMenuClose = () => {
        const { onCloseMenu } = this.props;
        this.setState({ menuOpen: false });
        if (onCloseMenu) {
            onCloseMenu();
        }
    };

    bindRef = (el) => (this.container = el);

    render() {
        const {
            primary,
            dropdown,
            style,
            label,
            labelStyle,
            large,
            disabled,
            iconStyle,
            desktop,
            focusState,
            segmented,
            menu,
            onClick,
            viewport,
            alignMenu,
            popoverStyle,
            anchorOrigin,
            onCloseMenu,
            dropdownIconStyles,
            // tslint:disable-next-line:trailing-comma
            ...other // prettier-ignore
        } = this.props;

        const isMobile = viewport === "mobile" || viewport === "phablet";
        const { menuOpen } = this.state;
        let disabledStyle = {};
        let disabledLabelStyle = {};

        let backgroundColor = primary ? theme.palette.accent1Color : "white";

        if (disabled) {
            if (primary) {
                backgroundColor = color(backgroundColor).desaturate(0.4).lighten(0.4).rgbString();
            } else {
                disabledStyle = {
                    border: "1px solid " + (theme.palette as any).lighterGrey,
                };
                disabledLabelStyle = {
                    color: (theme.palette as any).grey,
                };
            }
        }

        const hoverColor = primary
            ? color(theme.palette.accent1Color).darken(0.05).rgbString()
            : (theme.palette as any).lightestGrey;

        let button = (
            <FlatButton
                backgroundColor={backgroundColor}
                hoverColor={hoverColor}
                label={label || ""} // fix error: label or icon or children required
                labelPosition="before"
                disabled={disabled}
                onClick={menu && !segmented ? this.handleMenuOpen : onClick}
                icon={
                    dropdown ? (
                        <DropDownIcon color="#e1e1e1" style={{ ...styles.chevron, ...dropdownIconStyles }} />
                    ) : undefined
                }
                {...other}
                style={{
                    ...styles.base,
                    ...(large ? styles.baseLarge : {}),
                    ...(primary ? styles.basePrimary : {}),
                    ...(disabledStyle || {}),
                    ...(style || {}),
                }}
                labelStyle={{
                    ...styles.label,
                    ...(isMobile ? styles.labelMobile : {}),
                    ...(large ? styles.labelLarge : {}),
                    ...(primary ? styles.labelPrimary : {}),
                    ...(dropdown ? styles.dropdownLabel : {}),
                    ...(disabledLabelStyle || {}),
                    ...(labelStyle || {}),
                }}
            />
        );

        if (menu) {
            button = (
                <div
                    {...this.styles.base}
                    className={classNames({ buttonIsOpen: menuOpen })}
                    style={style}
                    ref={this.bindRef}
                >
                    {button}
                    <div className="buttonSegmented">
                        {segmented && <div className="buttonSeparator" />}
                        <IconButton disabled={disabled} onClick={this.handleMenuOpen}>
                            <DropDownIcon />
                        </IconButton>
                    </div>
                    <Popover
                        {...this.styles.paper}
                        className="button-menu"
                        open={menuOpen}
                        anchorEl={this.container}
                        PaperProps={{
                            style: popoverStyle || {
                                width: this.container ? this.container.clientWidth + "px" : "auto",
                            },
                        }}
                        anchorOrigin={
                            anchorOrigin || {
                                horizontal: alignMenu,
                                vertical: "bottom",
                            }
                        }
                        transformOrigin={{ horizontal: alignMenu, vertical: "top" }}
                        onClose={this.handleMenuClose}
                    >
                        {menu}
                    </Popover>
                </div>
            );
        }

        return button;
    }
}

export default Button;
